import { Link, Navigate, useParams } from 'react-router-dom'
import {ArrowRight, CheckCircle2, ArrowLeft, Quote} from 'lucide-react'
import Layout from '@/components/Layout'
import Container from '@/components/ui/Container'
import SectionHeading from '@/components/ui/SectionHeading'
import ConsultationForm from '@/components/ConsultationForm'
import { services } from '@/data/services'

function ServiceDetail() {
  const { slug } = useParams<{ slug: string }>()
  const service = services.find(s => s.slug === slug)

  if (!service)
    return <Navigate to="/servicos" replace />

  const Icon = service.icon
  const otherServices = services.filter(s => s.slug !== service.slug).slice(0, 3)

  return (
    <Layout>
      <section className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0">
          <img
            src={service.image}
            alt={service.title}
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/60" />
        </div>
        <Container className="relative py-20 sm:py-24">
          <Link to="/servicos" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300 hover:text-white">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Voltar para Serviços
          </Link>
          <span className="mt-6 flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-300">
            <Icon className="h-6 w-6" aria-hidden="true" />
          </span>
          <h1 className="mt-5 max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            {service.title}
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300">
            {service.description}
          </p>
          <div className="mt-8">
            <Link
              to="/contato"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-cyan-600/20 transition-colors hover:bg-cyan-500"
            >
              Solicitar consultoria sobre esta solução
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </Container>
      </section>

      <section className="py-20 sm:py-24">
        <Container className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-start">
          <div>
            <SectionHeading
              eyebrow="O que entregamos"
              title="Principais recursos da solução"
            />
            <ul className="mt-8 space-y-4">
              {service.features.map(feature => (
                <li key={feature} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-cyan-600" aria-hidden="true" />
                  <span className="text-sm leading-relaxed text-slate-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-8">
            <span className="text-xs font-semibold uppercase tracking-widest text-cyan-600">Estudo de Caso</span>
            <h3 className="mt-2 text-lg font-semibold text-slate-900">{service.caseStudy.client}</h3>
            <Quote className="mt-4 h-6 w-6 text-cyan-200" aria-hidden="true" />
            <div className="mt-3 space-y-4 text-sm leading-relaxed text-slate-600">
              <p><span className="font-semibold text-slate-800">Desafio: </span>{service.caseStudy.challenge}</p>
              <p><span className="font-semibold text-slate-800">Solução: </span>{service.caseStudy.solution}</p>
              <p><span className="font-semibold text-slate-800">Resultado: </span>{service.caseStudy.result}</p>
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-slate-50 py-20 sm:py-24">
        <Container>
          <SectionHeading
            eyebrow="Outras soluções"
            title="Você também pode se interessar por"
          />
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {otherServices.map((other) => {
              const OtherIcon = other.icon
              return (
                <Link
                  key={other.id}
                  to={`/servicos/${other.slug}`}
                  className="group rounded-2xl border border-slate-100 bg-white p-6 transition-all hover:border-cyan-200 hover:shadow-lg"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-50 text-cyan-600 transition-colors group-hover:bg-cyan-600 group-hover:text-white">
                    <OtherIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-slate-900">{other.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{other.shortDescription}</p>
                </Link>
              )
            })}
          </div>
        </Container>
      </section>

      <section className="py-20 sm:py-24">
        <Container className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
          <SectionHeading
            eyebrow="Solicitação de consultoria"
            title={`Pronto para implementar ${service.title.toLowerCase()}?`}
            description="Preencha o formulário e receba uma proposta personalizada de nossos consultores em até 24 horas úteis."
          />
          <div className="rounded-2xl border border-slate-100 p-6 shadow-sm sm:p-8">
            <ConsultationForm variant="full" />
          </div>
        </Container>
      </section>
    </Layout>
  )
}

export default ServiceDetail
